import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BackgroundOverlayComponent, BrandComponent, FallbackColor } from '@shared';
import { BrandConfigDTO, BrandImageTypeEnum, normalizeArrayToObject, normilizeBrandImage } from '@models';
import { MatButtonModule } from '@angular/material/button';
import { SkeletonTemplateService } from '@skeleton-loader';
import { catchError, EMPTY, map, tap } from 'rxjs';
import { HeroImageDataService, NavigationService } from '@core';

const FALLBACK_FONT_SIZE = 36;
const FALLBACK_COLOR: FallbackColor = 'white';

@Component({
  selector: 'app-split-banners',
  imports: [CommonModule, BackgroundOverlayComponent, MatButtonModule, BrandComponent],
  templateUrl: './split-banners.component.html',
  styleUrls: ['./split-banners.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitBannersComponent implements OnInit {
  brands = input<BrandConfigDTO[]>([]);
  private heroImageDataService = inject(HeroImageDataService);
  skeletonService = inject(SkeletonTemplateService);
  private navigationService = inject(NavigationService);
  brandsById = computed<Record<number, BrandConfigDTO>>(() => normalizeArrayToObject(this.brands(), 'id'));
  brandIds = computed<number[]>(() => this.brands()?.map(({ id }) => id));
  isLoading = signal<boolean>(true);
  splitBanners = signal<any[]>([]);
  fallbackFontSize = FALLBACK_FONT_SIZE;
  fallbackColor = FALLBACK_COLOR;

  getButtonLabel(brandName: string): string {
    return `Shop ${brandName}`;
  }

  goToBrandStore(banner: any): void {
    this.navigationService.goToBrandStore(banner.id);
  }

  private loadData() {
    this.heroImageDataService
      .getHeroImages(this.brandIds(), false)
      .pipe(
        map((brands) => brands.map(normilizeBrandImage)),
        map((banners) =>
          banners.map((banner) => ({
            ...banner,
            ariaLabel: `Shop all ${banner.name}`,
            brandLogoUrl: banner?.logoByType?.[BrandImageTypeEnum.White]?.url,
            imageUrl: this.brandsById()[banner.id]?.sourceSet,
          })),
        ),
        tap((banners) => {
          this.splitBanners.set(banners);
          this.isLoading.set(false);
        }),
        catchError((e) => EMPTY),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadData();
  }
}
